import { LazyQueryHookOptions } from '@apollo/client';

import {
  useSteeringWalletsQuery,
  useSteeringWalletQuery,
  useSurveysQuery,
  useWalletsForMembershipQuery,
  useGetBasicInfoSteeringWalletsLazyQuery,
  GetBasicInfoSteeringWalletsQuery,
  GetBasicInfoSteeringWalletsQueryVariables,
  useGetBasicInfoSteeringWalletsQuery,
} from '@occc/graphql';
import { usePaginatedQuery } from '@occc-app/hooks/usePaginatedQuery';

import { useMemo } from 'react';
import { GetMembershipWalletsParams, GetSurveysProps, GetWalletsProps } from './types';

import { useLazyQuery, useQuery } from '../utils';

export const useGetBasicInfoWalletsLazy = (
  baseOptions?: LazyQueryHookOptions<
    GetBasicInfoSteeringWalletsQuery,
    GetBasicInfoSteeringWalletsQueryVariables
  >,
) => {
  const [executeQuery, response] = useLazyQuery<
    GetBasicInfoSteeringWalletsQuery,
    GetBasicInfoSteeringWalletsQueryVariables
  >(useGetBasicInfoSteeringWalletsLazyQuery, baseOptions);

  return {
    executeQuery,
    response: {
      ...response,
      data: response.data?.steeringWallets.edges.map((e) => e.node),
    },
  };
};

export const useGetWallets = ({
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetWalletsProps = {}) => {
  const response = usePaginatedQuery(
    useSteeringWalletsQuery,
    'steeringWallets.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      ...baseOptions,
    },
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: response.data?.steeringWallets.edges.map((e) => e.node),
  };
};

export const useGetWallet = (uuid: string) => {
  const response = useSteeringWalletQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      uuid,
    },
  });

  return response;
};

export const useGetBasicInfoWalletsByIds = (uuidList: string[]) => {
  const response = useQuery(useGetBasicInfoSteeringWalletsQuery, {
    skip: uuidList.length === 0,
    variables: {
      filter: {
        uuidContains: uuidList,
      },
    },
  });

  return {
    ...response,
    data: response.data?.steeringWallets.edges.map((e) => e.node),
  };
};

export const useGetSurveysByWalletId = (walletId: string) => {
  const response = useSurveysQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      filter: {
        walletUuidEquals: walletId,
      },
    },
  });

  return {
    ...response,
    data: response.data?.surveys.edges.map((e) => e.node),
  };
};

export const useGetSurveys = ({
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetSurveysProps = {}) => {
  const response = usePaginatedQuery(
    useSurveysQuery,
    'surveys.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      ...baseOptions,
    },
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: response.data?.surveys.edges.map((e) => e.node),
  };
};

export const useGetWalletsForMembership = ({
  membershipId,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetMembershipWalletsParams) => {
  const response = usePaginatedQuery(
    useWalletsForMembershipQuery,
    'walletsForMembership.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        membershipId,
      },
      ...baseOptions,
    },
    paginationInfo,
    onPageChange,
  );

  const data = useMemo(() => {
    return response.data?.walletsForMembership.edges.map((e) => e.node);
  }, [response.data]);

  return {
    ...response,
    data,
  };
};
